import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, ImagePlus, Loader2, X, Pencil } from 'lucide-react';
import { GlowCard, ContinueButton, QuestionHeader, HEAD, ORANGE, FooterPortal } from './IntakeUI';
import { SOMEONE_HOME, ACCESS, OPTION_LABEL, SERVICE_CARDS, visibleQuestions, PATHWAYS } from './pathways';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Single choice (photo / minimal / number / binary) — auto-advances after tactile pause */
export const SingleChoice = ({ q, value, onAnswer }) => {
  const [tapped, setTapped] = useState(null);
  const pick = (v) => {
    if (tapped !== null) return;
    setTapped(v);
    setTimeout(() => { onAnswer(q.key, v); setTapped(null); }, 380);
  };
  const cols = q.type === 'binary' ? 'grid-cols-2' : q.type === 'photo' ? 'grid-cols-2' : q.type === 'number' ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2';
  return (
    <div className={`grid ${cols} gap-3 sm:gap-4`} role="radiogroup" aria-label={q.title}>
      {q.options.map(o => (
        <GlowCard key={String(o.value)} variant={q.type} img={o.img} Icon={o.icon} label={o.label} sub={o.sub} desc={o.desc}
          selected={String(value) === String(o.value)} tapped={String(tapped) === String(o.value)} onSelect={() => pick(o.value)}
          testId={`opt-${q.key}-${String(o.value).replace(/[^a-z0-9_.-]/gi, '')}`} className={o.wide ? 'col-span-2 sm:col-span-2 !aspect-auto min-h-[104px]' : ''} />
      ))}
    </div>
  );
};

export const MultiChoice = ({ q, value = [], onChange, onContinue }) => {
  const toggle = (v) => onChange(q.key, value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:gap-4" role="group" aria-label={q.title}>
        {q.options.map(o => (
          <GlowCard key={o.value} variant="multi" role="checkbox" img={q.minimal ? undefined : o.img} Icon={o.icon} label={o.label} selected={value.includes(o.value)} onSelect={() => toggle(o.value)} testId={`opt-${q.key}-${o.value}`} className={!q.minimal && !o.img ? 'aspect-[4/3]' : ''} />
        ))}
      </div>
      <FooterPortal><ContinueButton onClick={onContinue} /></FooterPortal>
    </>
  );
};

export const TextQuestion = ({ q, value = '', onChange, onContinue }) => (
  <>
    <textarea value={value} onChange={e => onChange(q.key, e.target.value.slice(0, 600))} rows={5} placeholder={q.placeholder} maxLength={600} aria-label={q.title} data-testid={`input-${q.key}`}
      className="w-full rounded-2xl bg-zinc-950 text-white placeholder:text-zinc-500 p-4 text-base focus:outline-none" style={{ border: `2px solid rgba(245,111,5,0.6)`, boxShadow: '0 0 14px rgba(245,111,5,0.25)' }} />
    <div className="mt-2 flex items-center justify-between text-xs text-zinc-500"><span>{q.optional ? 'Optional' : ''}</span><span aria-live="polite">{value.length} / 600</span></div>
    <FooterPortal><ContinueButton onClick={onContinue} disabled={!q.optional && !value.trim()} label={q.optional && !value ? 'Skip' : 'Continue'} /></FooterPortal>
  </>
);

export const DateQuestion = ({ q, value = '', onChange, onContinue }) => {
  const min = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  return (
    <>
      <input type={q.type === 'time' ? 'time' : 'date'} min={q.type === 'time' ? undefined : min} value={value} onChange={e => onChange(q.key, e.target.value)} aria-label={q.title} data-testid={`input-${q.key}`}
        className="w-full h-16 rounded-2xl bg-zinc-950 text-white px-5 text-xl font-bold focus:outline-none [color-scheme:dark]" style={{ ...HEAD, border: `2px solid ${ORANGE}`, boxShadow: '0 0 18px rgba(245,111,5,0.35)' }} />
      <FooterPortal><ContinueButton onClick={onContinue} disabled={!q.optional && !value} label={q.optional && !value ? 'Skip' : 'Continue'} /></FooterPortal>
    </>
  );
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const contactError = (d) => {
  if (!(d.contact_name || '').trim()) return 'Please enter your name.';
  const digits = (d.contact_phone || '').replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 15) return 'Please enter a valid phone number, including area code.';
  if (!EMAIL_RE.test((d.contact_email || '').trim())) return 'Please enter a valid email address.';
  return '';
};

const CONTACT_FIELDS = [['contact_name', 'Full name', 'text', 'name', 120], ['contact_phone', 'Phone number', 'tel', 'tel', 30], ['contact_email', 'Email address', 'email', 'email', 254]];

export const ContactQuestion = ({ data, onChange, onContinue }) => {
  const err = contactError(data);
  return (
    <>
      <div className="space-y-4">
        {CONTACT_FIELDS.map(([k, label, type, ac, max]) => (
          <label key={k} className="block">
            <span className="text-xs font-extrabold uppercase tracking-[0.25em] text-zinc-400" style={HEAD}>{label}</span>
            <input type={type} autoComplete={ac} maxLength={max} value={data[k] || ''} onChange={e => onChange(k, e.target.value)} data-testid={`input-${k}`}
              className="mt-2 w-full h-14 rounded-2xl bg-zinc-950 text-white px-5 text-lg font-semibold focus:outline-none" style={{ border: `2px solid ${ORANGE}`, boxShadow: '0 0 18px rgba(245,111,5,0.35)' }} />
          </label>
        ))}
      </div>
      {err && (data.contact_name || data.contact_phone || data.contact_email) && <p className="mt-3 text-sm text-orange-300" role="alert" data-testid="contact-confirm-error">{err}</p>}
      <FooterPortal><ContinueButton onClick={onContinue} disabled={!!err} /></FooterPortal>
    </>
  );
};

const AccessGroup = ({ label, k, opts, data, onChange }) => (
  <div className="mb-6">
    <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-zinc-400 mb-3" style={HEAD}>{label}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label={label}>
      {opts.map(o => <GlowCard key={o.value} label={o.label} Icon={o.icon} selected={data[k] === o.value} onSelect={() => onChange(k, o.value)} testId={`opt-${k}-${o.value}`} />)}
    </div>
  </div>
);

export const InputQuestion = ({ q, value = '', onChange, onContinue }) => (
  <>
    <input type="text" value={value} onChange={e => onChange(q.key, e.target.value.slice(0, 200))} placeholder={q.placeholder} maxLength={200} aria-label={q.title} data-testid={`input-${q.key}`}
      className="w-full h-16 rounded-2xl bg-zinc-950 text-white placeholder:text-zinc-500 px-5 text-lg font-semibold focus:outline-none" style={{ border: `2px solid ${ORANGE}`, boxShadow: '0 0 18px rgba(245,111,5,0.35)' }} />
    <FooterPortal><ContinueButton onClick={onContinue} disabled={!q.optional && !value.trim()} label={q.optional && !value ? 'Skip' : 'Continue'} /></FooterPortal>
  </>
);

export const AccessQuestion = ({ data, onChange, onContinue }) => {
  return (
    <>
      <AccessGroup label="Will anyone be there?" k="someone_home" opts={SOMEONE_HOME} data={data} onChange={onChange} />
      <AccessGroup label="How do we get in?" k="access_method" opts={ACCESS} data={data} onChange={onChange} />
      <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-zinc-400 mb-3" style={HEAD}>Entry instructions <span className="text-zinc-600 normal-case tracking-normal font-medium">(optional · only your Gleam Guru team can see this)</span></p>
      <textarea value={data.entry_instructions || ''} onChange={e => onChange('entry_instructions', e.target.value.slice(0, 600))} rows={4} placeholder="Lockbox location, codes, alarm details, where to park…" aria-label="Entry instructions" data-testid="input-entry_instructions"
        className="w-full rounded-2xl bg-zinc-950 text-white placeholder:text-zinc-500 p-4 text-base focus:outline-none" style={{ border: `2px solid rgba(245,111,5,0.6)`, boxShadow: '0 0 14px rgba(245,111,5,0.25)' }} />
      <FooterPortal><ContinueButton onClick={onContinue} disabled={!data.someone_home || !data.access_method} /></FooterPortal>
    </>
  );
};

export const PhotosQuestion = ({ q = {}, token, photos, setPhotos, onContinue }) => {
  const max = q.maxPhotos || 12;
  const fileRef = useRef(null); const camRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const upload = async (files) => {
    const all = Array.from(files || []);
    const tooBig = q.maxMB ? all.filter(f => f.size > q.maxMB * 1024 * 1024) : [];
    if (tooBig.length) toast.error(`Photos must be ${q.maxMB} MB or smaller. Skipped ${tooBig.map(f => f.name).join(', ')}.`);
    const list = all.filter(f => !tooBig.includes(f)).slice(0, max - photos.length);
    if (!list.length) { if (!tooBig.length) toast.error(`You can upload up to ${max} photos.`); return; }
    setUploading(list.length);
    for (const f of list) {
      const fd = new FormData(); fd.append('file', f);
      try { const r = await axios.post(`${API_URL}/api/intake/${token}/photos`, fd); setPhotos(p => [...p, { id: r.data.id, url: URL.createObjectURL(f) }]); }
      catch (e) { toast.error(e.response?.data?.detail || `Couldn't upload ${f.name}.`); }
      finally { setUploading(n => n - 1); }
    }
  };
  const remove = async (p) => { try { await axios.delete(`${API_URL}/api/intake/${token}/photos/${p.id}`); setPhotos(prev => prev.filter(x => x.id !== p.id)); } catch { toast.error("Couldn't remove that photo."); } };
  const Tile = ({ onClick, Icon, label, testId, className = '' }) => (
    <button type="button" onClick={onClick} data-testid={testId} className={`h-28 rounded-2xl bg-zinc-950 flex flex-col items-center justify-center gap-2 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${className}`} style={{ border: `2px dashed rgba(245,111,5,0.7)`, boxShadow: '0 0 14px rgba(245,111,5,0.25)' }}>
      <Icon className="w-7 h-7" style={{ color: ORANGE }} /><span className="text-xs font-extrabold uppercase tracking-wider" style={HEAD}>{label}</span>
    </button>
  );
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Tile onClick={() => camRef.current?.click()} Icon={Camera} label="Take a photo" testId="photo-camera-btn" className="sm:hidden" />
        <Tile onClick={() => fileRef.current?.click()} Icon={ImagePlus} label="Upload photos" testId="photo-upload-btn" className="sm:col-span-2" />
      </div>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => { upload(e.target.files); e.target.value = ''; }} data-testid="photo-camera-input" />
      <input ref={fileRef} type="file" accept={q.accept || 'image/jpeg,image/png,image/webp,image/heic,image/heif'} multiple className="hidden" onChange={e => { upload(e.target.files); e.target.value = ''; }} data-testid="photo-file-input" />
      {(photos.length > 0 || uploading > 0) && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 mt-5" data-testid="photo-grid">
          {photos.map(p => (
            <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden border border-zinc-700" data-testid={`photo-${p.id}`}>
              <img src={p.url} alt="Uploaded space photo" className="w-full h-full object-cover" />
              <button type="button" onClick={() => remove(p)} aria-label="Remove photo" className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/80 text-white flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {Array.from({ length: uploading }).map((_, i) => <div key={i} className="aspect-square rounded-xl border border-zinc-800 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>)}
        </div>
      )}
      <p className="text-xs text-zinc-500 mt-3" data-testid="photo-count">{photos.length} of {max} photos</p>
      <FooterPortal><ContinueButton onClick={onContinue} label={photos.length ? 'Continue' : 'Skip for now'} /></FooterPortal>
    </>
  );
};

/* Review & Submit */
export const ReviewQuestion = ({ pathway, data, photos, goTo, onSubmit, submitting, initialRequest }) => {
  const qs = visibleQuestions(pathway, data).filter(q => !['review', 'photos'].includes(q.type));
  const val = (q) => {
    if (q.type === 'contact') return [data.contact_name, data.contact_phone, data.contact_email].filter(Boolean).join(' · ');
    if (q.type === 'access') return [OPTION_LABEL(SOMEONE_HOME, data.someone_home), OPTION_LABEL(ACCESS, data.access_method), data.entry_instructions ? 'Entry instructions provided' : null].filter(Boolean).join(' · ');
    if (q.type === 'multi') return (data[q.key] || []).map(v => OPTION_LABEL(q.options, v)).join(', ');
    if (['text', 'date', 'time', 'input'].includes(q.type)) return data[q.key];
    return OPTION_LABEL(q.options, data[q.key]);
  };
  const stages = {};
  qs.forEach(q => { const v = val(q); if (v) (stages[q.stage] = stages[q.stage] || []).push({ q, v }); });
  const stageNames = PATHWAYS[pathway]?.stages || [];
  return (
    <>
      <div className="rounded-2xl bg-zinc-950 p-4 mb-3" style={{ border: `2px solid ${ORANGE}` }} data-testid="review-service">
        <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-bold" style={HEAD}>Service</p>
        <p className="gg-display text-2xl">{SERVICE_CARDS[pathway]?.label}</p>
      </div>
      <div className="space-y-3">
        {Object.entries(stages).map(([si, rows]) => (
          <section key={si} className="rounded-2xl bg-zinc-950 border border-zinc-800 p-4" data-testid={`review-stage-${si}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs tracking-[0.25em] uppercase font-bold" style={{ ...HEAD, color: ORANGE }}>{stageNames[si]}</h3>
              <button type="button" onClick={() => goTo(rows[0].q.key)} data-testid={`review-stage-${si}-edit`} className="inline-flex items-center gap-1 text-xs font-bold text-zinc-300 hover:text-white"><Pencil className="w-3 h-3" /> Edit</button>
            </div>
            {rows.map(({ q, v }) => (
              <div key={q.key} className="flex justify-between gap-4 py-1.5 border-b border-zinc-900 last:border-0 text-sm">
                <span className="text-zinc-500 truncate">{q.title.replace(/\?$/, '')}</span><span className="text-white font-semibold text-right">{v}</span>
              </div>
            ))}
          </section>
        ))}
        {initialRequest?.message && (
          <section className="rounded-2xl bg-zinc-950 border border-zinc-800 p-4" data-testid="review-initial-message">
            <h3 className="text-xs tracking-[0.25em] uppercase font-bold mb-2" style={{ ...HEAD, color: ORANGE }}>Your first message</h3>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{initialRequest.message}</p>
          </section>
        )}
        <section className="rounded-2xl bg-zinc-950 border border-zinc-800 p-4" data-testid="review-photos">
          <div className="flex items-center justify-between mb-2"><h3 className="text-xs tracking-[0.25em] uppercase font-bold" style={{ ...HEAD, color: ORANGE }}>Photos ({photos.length})</h3><button type="button" onClick={() => goTo('photos')} data-testid="review-photos-edit" className="inline-flex items-center gap-1 text-xs font-bold text-zinc-300 hover:text-white"><Pencil className="w-3 h-3" /> Edit</button></div>
          {photos.length ? <div className="grid grid-cols-5 gap-2">{photos.map(p => <img key={p.id} src={p.url} alt="" className="aspect-square w-full rounded-lg object-cover" />)}</div> : <p className="text-sm text-zinc-500">No photos added</p>}
        </section>
      </div>
      <FooterPortal><ContinueButton onClick={onSubmit} loading={submitting} label="Send to Gleam Guru" /></FooterPortal>
    </>
  );
};

export const QuestionScreen = ({ q, crumbs, children }) => (
  <div data-testid={`screen-${q.key}`}>
    <QuestionHeader crumbs={crumbs} eyebrow={q.eyebrow} title={q.title} subtitle={q.subtitle} />
    {children}
  </div>
);
